import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import { WIRE_SORT, type NotificationPage, type SortMode } from './types';

/**
 * The inbox read.
 *
 * **No `pageSize` is sent.** The page size is `NOTIFICATION_PAGE_SIZE_DEFAULT`
 * on the owning service, and a number chosen here would put a configured value
 * behind a rebuild of this bundle. The page comes back declaring the size it
 * used, and the pager reads that.
 *
 * **No `isRead` is sent either.** The surface has a sort control, not a filter:
 * «unread first» reorders the page it is holding, and asking the service for
 * unread rows only would answer a different question by dropping every read row
 * off the screen.
 *
 * The cursor is opaque — an encoding the service owns — so the console holds the
 * ones it has been handed and never composes one.
 */
export function notificationsKey(sort: SortMode, cursor: string | null) {
  return key('notification', 'notifications', { sort, cursor });
}

/**
 * Every page of every sort at once — what a change of view invalidates.
 *
 * A read that failed stays cached under its own key, so re-selecting a sort the
 * operator had already tried would show the old failure and issue nothing.
 */
export function notificationsRootKey() {
  return key('notification', 'notifications');
}

export function useNotifications(sort: SortMode, cursor: string | null) {
  return useQuery({
    queryKey: notificationsKey(sort, cursor),
    queryFn: () => {
      const params = new URLSearchParams({ sort: WIRE_SORT[sort] });
      if (cursor) params.set('cursor', cursor);
      return api.get<NotificationPage>('notification', `/notifications?${params.toString()}`);
    },
    // A queue's depth is what an operator came back to the tab for.
    staleTime: STALE_TIME.list,
    // Paging keeps the previous page on screen while the next one loads, so the
    // table does not blink through a skeleton on every press of «التالي».
    placeholderData: keepPreviousData,
  });
}
